import {
  ColoringContextEnum,
  SVGLineStyleEnum,
  VoltageColors,
  VoltageLevelEnum,
} from '~/composables/coloring/constants'
import type { LineStyleSetting, ColoringSettingGantt, ColoringSettingSVG } from '~/composables/coloring/constants'
import {
  ProjectRead,
  SwitchingCategoryTypeEnum,
  ThemeEnum,
  UserRead,
  EquipmentTypeEnum,
} from '@gridcon-energy/schaltungstool-backend'
import type { SettingsBarProperty, SwitchingSettingsPeriodInitialProperty } from '~/types'
import { defu } from 'defu'
import { RemoveOptional } from '~/types/helpers'
import { createNumberToHexColorFn, hexWithOpacity } from '~/helper/colors'
import { BaseColorPalette, PremisesColorPalette } from '~/lib/constants'
import { OfflineStatusEnum } from '~/lib/switchings'
import { EquipmentNetworkStateEnum, EquipmentStatusEnum } from '~/lib/equipments'

export type AppSettingsUser = {
  useDrawerNavigation: boolean
  drawerExpanded: boolean
}

export type AppSettingsDesign = {
  // Switching coloring
  coloring: {
    [ColoringContextEnum.GANTT]: ColoringSettingGantt
    [ColoringContextEnum.SVG]: ColoringSettingSVG
  }
  coloringOfflineRange: {
    [ColoringContextEnum.GANTT]: Record<OfflineStatusEnum, string>
    [ColoringContextEnum.SVG]: Record<OfflineStatusEnum, string>
  }
  coloringSVGNetworkState: {
    [EquipmentNetworkStateEnum.OFFLINE]: LineStyleSetting
    [EquipmentNetworkStateEnum.ONLINE]: LineStyleSetting
  }
  coloringEquipmentStatus: {
    [EquipmentStatusEnum.EXISTING]: LineStyleSetting
    [EquipmentStatusEnum.ADDITION]: LineStyleSetting
    [EquipmentStatusEnum.DISMANTLING]: LineStyleSetting
    [EquipmentStatusEnum.TEMP_AVAILABLE]: LineStyleSetting
    // [EquipmentStatusEnum.TEMP_NOT_AVAILABLE]: LineStyleSetting
    [EquipmentStatusEnum.UNKNOWN]: LineStyleSetting
  }

  // Restrictions - Schaltungsblockprämissen
  switchingBlockRestrictionPrevious: string
  switchingBlockRestrictionParallelForbidden: string

  // Violations - Schaltungsprämissen
  switchingPremiseDynamicProtective: string
  switchingPremiseDynamicOffline: string
  switchingPremiseDynamicOnline: string

  switchingPremiseStaticConsecutiveActivated: string
  switchingPremiseStaticConsecutiveDeactivated: string

  // Limit Violation
  switchingLimitViolation: string

  // TBT Daily stripe pattern
  switchingInfoTbtDaily: {
    color: string
    spacing: number
    angle: number
  }

  // initial period
  periodInitial: SwitchingSettingsPeriodInitialProperty

  // possible period (background bars)
  periodPossible: SettingsBarProperty

  // optimized period
  periodOptimized: SettingsBarProperty

  ///////////////////////////////////////////////////////////////////////////////////////////////////////
  // Following values are deprecated, there is a migration. At some point, these values can be deleted //
  ///////////////////////////////////////////////////////////////////////////////////////////////////////

  switchingTypes?: { [key in SwitchingCategoryTypeEnum]: string } // replaced by switchingTypesGantt
  timeFrameInitial?: SwitchingSettingsPeriodInitialProperty // replaced by periodInitial
  timeframePossible?: SettingsBarProperty // replaced by periodPossible
  timeframeStartEnd?: SettingsBarProperty // replaced by periodOptimized

  switchingPremiseProtective?: string // replaced by switchingPremiseDynamicProtective
  switchingPremiseOffline?: string // replaced by switchingPremiseDynamicOffline
  switchingPremiseOnline?: string // replaced by switchingPremiseDynamicOnline

  switchingTypesGantt?: { [key in SwitchingCategoryTypeEnum]: string } // replaced by coloring.gantt.category
  switchingTypesSVG?: { [key in SwitchingCategoryTypeEnum]: string } // replaced by coloring.svg.category[].color
  switchingTypesSVGLineStyle?: {
    [key in SwitchingCategoryTypeEnum]: SVGLineStyleEnum
  } // replaced by coloring.svg.category[].lineStyle
}
export type AppSettings = {
  theme: ThemeEnum
  design: AppSettingsDesign
  settings: AppSettingsUser
}
/**
 * Load the settings of user object
 */
export function loadUserSettings(user: UserRead | null, defaults: AppSettings): AppSettings {
  const settings = defaults
  if (user?.theme) {
    settings.theme = user.theme
  }
  if (user?.settings) {
    settings.settings = defu(user.settings, defaults.settings)
  }
  if (user?.design) {
    const design: AppSettingsDesign = defu(user.design, defaults.design)
    delete (design as AppSettingsDesign & { gridOpacity?: number }).gridOpacity

    // migrate old values
    if (design.timeFrameInitial) design.periodInitial = design.timeFrameInitial
    if (design.timeframePossible) design.periodPossible = design.timeframePossible
    if (design.timeframeStartEnd) design.periodOptimized = design.timeframeStartEnd
    if (design.switchingTypes)
      design.switchingTypesGantt = {
        ...design.switchingTypes,
        ...design.switchingTypesGantt,
      }

    delete design.switchingTypes
    delete design.timeFrameInitial
    delete design.timeframePossible
    delete design.timeframeStartEnd

    if (design.switchingPremiseProtective) design.switchingPremiseDynamicProtective = design.switchingPremiseProtective
    if (design.switchingPremiseOffline) design.switchingPremiseDynamicOffline = design.switchingPremiseOffline
    if (design.switchingPremiseOnline) design.switchingPremiseDynamicOnline = design.switchingPremiseOnline

    delete design.switchingPremiseProtective
    delete design.switchingPremiseOffline
    delete design.switchingPremiseOnline

    // migrate coloring
    if (design.switchingTypesGantt) {
      design.coloring.gantt.category.activated.color = design.switchingTypesGantt.activated
      design.coloring.gantt.category.deactivated.color = design.switchingTypesGantt.deactivated
      design.coloring.gantt.category.disabled.color = design.switchingTypesGantt.disabled
      design.coloring.gantt.category.protected.color = design.switchingTypesGantt.protected
      design.coloring.gantt.category.meta.color = design.switchingTypesGantt.meta
      design.coloring.gantt.category.bypassMode.color = design.switchingTypesGantt.bypassMode
      delete design.switchingTypesGantt
    }

    if (design.switchingTypesSVG) {
      design.coloring.svg.category.activated.color = design.switchingTypesSVG.activated
      design.coloring.svg.category.deactivated.color = design.switchingTypesSVG.deactivated
      design.coloring.svg.category.disabled.color = design.switchingTypesSVG.disabled
      design.coloring.svg.category.protected.color = design.switchingTypesSVG.protected
      design.coloring.svg.category.meta.color = design.switchingTypesSVG.meta
      design.coloring.svg.category.bypassMode.color = design.switchingTypesSVG.bypassMode
      delete design.switchingTypesSVG
    }

    if (design.switchingTypesSVGLineStyle) {
      design.coloring.svg.category.activated.lineStyle = design.switchingTypesSVGLineStyle.activated
      design.coloring.svg.category.deactivated.lineStyle = design.switchingTypesSVGLineStyle.deactivated
      design.coloring.svg.category.disabled.lineStyle = design.switchingTypesSVGLineStyle.disabled
      design.coloring.svg.category.protected.lineStyle = design.switchingTypesSVGLineStyle.protected
      design.coloring.svg.category.meta.lineStyle = design.switchingTypesSVGLineStyle.meta
      design.coloring.svg.category.bypassMode.lineStyle = design.switchingTypesSVGLineStyle.bypassMode
      delete design.switchingTypesSVGLineStyle
    }
    settings.design = design
  }
  return settings
}

export function createDefaultSettings(projects: ProjectRead[]): AppSettings {
  const coloring: AppSettingsDesign['coloring'] = {
    gantt: {
      category: {
        [SwitchingCategoryTypeEnum.Deactivated]: {
          color: hexWithOpacity(BaseColorPalette.ORANGE, 0.8),
          // sync: false,
        }, // Außerbetriebnahme
        [SwitchingCategoryTypeEnum.Activated]: {
          color: hexWithOpacity(BaseColorPalette.GREEN, 0.8),
          // sync: false,
        }, // Inbetriebnahme
        [SwitchingCategoryTypeEnum.Disabled]: {
          color: hexWithOpacity(BaseColorPalette.BLUE, 0.8),
          // sync: false,
        }, // Freischalten
        [SwitchingCategoryTypeEnum.Protected]: {
          color: hexWithOpacity(BaseColorPalette.MUSTARD, 0.8),
          // sync: false,
        }, // Schutzschalten
        [SwitchingCategoryTypeEnum.Meta]: {
          color: hexWithOpacity(BaseColorPalette.GREY, 0.8),
          // sync: false,
        }, // Meta
        [SwitchingCategoryTypeEnum.BypassMode]: {
          color: hexWithOpacity(BaseColorPalette.RED, 0.8),
          // sync: false,
        }, // US-Betrieb
      },
      project: {
        default: {
          color: '#7d7d7d',
          // sync: false,
        },
      },
      voltage: {
        [VoltageLevelEnum.default]: {
          color: VoltageColors.default,
          // sync: false,
        },
        [VoltageLevelEnum.KV_110]: {
          color: VoltageColors[VoltageLevelEnum.KV_110],
          // sync: false,
        },
        [VoltageLevelEnum.KV_220]: {
          color: VoltageColors[VoltageLevelEnum.KV_220],
          // sync: false,
        },
        [VoltageLevelEnum.KV_380]: {
          color: VoltageColors[VoltageLevelEnum.KV_380],
          // sync: false,
        },
      },
      equipment_type: {
        [EquipmentTypeEnum.Transformator]: {
          color: BaseColorPalette.CYAN,
        },
        [EquipmentTypeEnum.PowerPlant]: {
          color: BaseColorPalette.GREEN,
        },
        [EquipmentTypeEnum.PowerLine]: {
          color: BaseColorPalette.BLUE,
        },
        [EquipmentTypeEnum.OverheadPowerLine]: {
          color: BaseColorPalette.PURPLE,
        },
        [EquipmentTypeEnum.UndergroundPowerLine]: {
          color: BaseColorPalette.MUSTARD,
        },
        [EquipmentTypeEnum.Busbar]: {
          color: BaseColorPalette.ORANGE,
        },
        [EquipmentTypeEnum.ConnectorSocket]: {
          color: BaseColorPalette.GREY,
        },
        [EquipmentTypeEnum.CompensationSystem]: {
          color: BaseColorPalette.RED,
        },
        [EquipmentTypeEnum.Field]: {
          color: '#ff00c0',
        },
        [EquipmentTypeEnum.Misc]: {
          color: '#ecf0f1',
        },
      },
    },
    svg: {
      category: {
        [SwitchingCategoryTypeEnum.Deactivated]: {
          color: BaseColorPalette.ORANGE,
          lineStyle: SVGLineStyleEnum.FILL,
        }, // Außerbetriebnahme
        [SwitchingCategoryTypeEnum.Activated]: {
          color: BaseColorPalette.GREEN,
          lineStyle: SVGLineStyleEnum.FILL,
        }, // Inbetriebnahme
        [SwitchingCategoryTypeEnum.Disabled]: {
          color: BaseColorPalette.BLUE,
          lineStyle: SVGLineStyleEnum.FILL,
        }, // Freischalten
        [SwitchingCategoryTypeEnum.Protected]: {
          color: BaseColorPalette.MUSTARD,
          lineStyle: SVGLineStyleEnum.FILL,
        }, // Schutzschalten
        [SwitchingCategoryTypeEnum.Meta]: {
          color: BaseColorPalette.GREY,
          lineStyle: SVGLineStyleEnum.FILL,
        }, // Meta
        [SwitchingCategoryTypeEnum.BypassMode]: {
          color: BaseColorPalette.RED,
          lineStyle: SVGLineStyleEnum.FILL,
        }, // US-Betrieb
      },
      project: {
        default: { color: '#7d7d7d', lineStyle: SVGLineStyleEnum.FILL },
      },
      voltage: {
        [VoltageLevelEnum.default]: {
          color: VoltageColors.default,
          lineStyle: SVGLineStyleEnum.FILL,
        },
        [VoltageLevelEnum.KV_110]: {
          color: VoltageColors[VoltageLevelEnum.KV_110],
          lineStyle: SVGLineStyleEnum.FILL,
        },
        [VoltageLevelEnum.KV_220]: {
          color: VoltageColors[VoltageLevelEnum.KV_220],
          lineStyle: SVGLineStyleEnum.FILL,
        },
        [VoltageLevelEnum.KV_380]: {
          color: VoltageColors[VoltageLevelEnum.KV_380],
          lineStyle: SVGLineStyleEnum.FILL,
        },
      },
      equipment_type: {
        [EquipmentTypeEnum.Transformator]: {
          color: BaseColorPalette.CYAN,
          lineStyle: SVGLineStyleEnum.FILL,
        },
        [EquipmentTypeEnum.PowerPlant]: {
          color: BaseColorPalette.GREEN,
          lineStyle: SVGLineStyleEnum.FILL,
        },
        [EquipmentTypeEnum.PowerLine]: {
          color: BaseColorPalette.BLUE,
          lineStyle: SVGLineStyleEnum.FILL,
        },
        [EquipmentTypeEnum.OverheadPowerLine]: {
          color: BaseColorPalette.PURPLE,
          lineStyle: SVGLineStyleEnum.FILL,
        },
        [EquipmentTypeEnum.UndergroundPowerLine]: {
          color: BaseColorPalette.MUSTARD,
          lineStyle: SVGLineStyleEnum.FILL,
        },
        [EquipmentTypeEnum.Busbar]: {
          color: BaseColorPalette.ORANGE,
          lineStyle: SVGLineStyleEnum.FILL,
        },
        [EquipmentTypeEnum.ConnectorSocket]: {
          color: BaseColorPalette.GREY,
          lineStyle: SVGLineStyleEnum.FILL,
        },
        [EquipmentTypeEnum.CompensationSystem]: {
          color: BaseColorPalette.RED,
          lineStyle: SVGLineStyleEnum.FILL,
        },
        [EquipmentTypeEnum.Field]: {
          color: '#ff00c0',
          lineStyle: SVGLineStyleEnum.FILL,
        },
        [EquipmentTypeEnum.Misc]: {
          color: '#ecf0f1',
          lineStyle: SVGLineStyleEnum.FILL,
        },
      },
    },
  }

  const colorFn = createNumberToHexColorFn()
  for (const project of projects) {
    const projectColor = colorFn(project.id)
    coloring.gantt.project[project.id] = {
      color: projectColor,
      // sync: false,
    }
    coloring.svg.project[project.id] = {
      color: projectColor,
      lineStyle: SVGLineStyleEnum.FILL,
    }
  }

  const coloringOfflineRange: AppSettingsDesign['coloringOfflineRange'] = {
    gantt: {
      [OfflineStatusEnum.pendingActivation]: '#818181', // '#2ECC714D',
      [OfflineStatusEnum.deactivated]: '#818181', // '#E67E224D',
      [OfflineStatusEnum.temporary]: '#818181',
    },
    svg: {
      [OfflineStatusEnum.pendingActivation]: '#818181', // '#2ECC714D',
      [OfflineStatusEnum.deactivated]: '#818181', // '#E67E224D',
      [OfflineStatusEnum.temporary]: '#818181',
    },
  }

  const coloringSVGNetworkState: AppSettingsDesign['coloringSVGNetworkState'] = {
    [EquipmentNetworkStateEnum.OFFLINE]: {
      color: '#0000004C',
      lineStyle: SVGLineStyleEnum.FILL,
    },
    [EquipmentNetworkStateEnum.ONLINE]: {
      color: '#00c800',
      lineStyle: SVGLineStyleEnum.FILL,
    },
  }

  const coloringEquipmentStatus: AppSettingsDesign['coloringEquipmentStatus'] = {
    [EquipmentStatusEnum.EXISTING]: {
      color: '#0000004C',
      lineStyle: SVGLineStyleEnum.FILL,
    },
    [EquipmentStatusEnum.ADDITION]: {
      color: '#4caf50',
      lineStyle: SVGLineStyleEnum.FILL,
    },
    [EquipmentStatusEnum.DISMANTLING]: {
      color: '#9E9E9E4C',
      lineStyle: SVGLineStyleEnum.FILL,
    },
    [EquipmentStatusEnum.TEMP_AVAILABLE]: {
      color: '#fb8c00',
      lineStyle: SVGLineStyleEnum.FILL,
    },
    // [EquipmentStatusEnum.TEMP_NOT_AVAILABLE]: {
    //   color: '#fb8c00',
    //   lineStyle: SVGLineStyleEnum.FILL,
    // },
    [EquipmentStatusEnum.UNKNOWN]: {
      color: '#fb4300',
      lineStyle: SVGLineStyleEnum.FILL,
    },
  }

  // Optional keys should be deprecated keys we don't want in default settings
  const design: RemoveOptional<AppSettingsDesign> = {
    coloring,
    coloringOfflineRange,
    coloringSVGNetworkState,
    coloringEquipmentStatus,

    switchingPremiseDynamicProtective: BaseColorPalette.GREEN, // Schaltungsprämisse Schutzschaltung
    switchingPremiseDynamicOffline: BaseColorPalette.ORANGE, // Schaltungsprämisse Offline
    switchingPremiseDynamicOnline: BaseColorPalette.PURPLE, // Schaltungsprämisse Online

    switchingPremiseStaticConsecutiveActivated: BaseColorPalette.GREEN,
    switchingPremiseStaticConsecutiveDeactivated: BaseColorPalette.ORANGE,

    // TBT Daily stripe pattern
    switchingInfoTbtDaily: {
      color: '#C3E7FF',
      spacing: 4,
      angle: 135,
    },

    switchingBlockRestrictionPrevious: PremisesColorPalette.PURPLE_DARK, // Schaltungsblockprämisse "Nicht-seriell"
    switchingBlockRestrictionParallelForbidden: PremisesColorPalette.RED, // Schaltungsblockprämisse "Nicht-parallel"
    switchingLimitViolation: '#ff0063', // Limit Violation warning color

    periodInitial: {
      color: '#808080',
      width: 40,
    },

    periodPossible: {
      height: 20,
      opacity: 10,
    },
    periodOptimized: {
      height: 80,
      opacity: 100,
    },
  }

  return {
    theme: ThemeEnum.Auto,
    design,
    settings: {
      useDrawerNavigation: false,
      drawerExpanded: false,
    },
  }
}
